import { Link } from 'react-router';
import { REMINDER_LABELS } from '../data/reminderSchedule';
import type { ReminderEntry } from '../data/reminders';
import { formatDay } from './dates';
import { PagerBar, PagerTop, usePager } from './Pager';

/**
 * B5 (owner rulings 2026-10-25): who has been emailed about a store or task —
 * reminders, escalations, "assigned to you" and reopen emails, newest first,
 * recipients by name. `entries` null = still loading; `failed` = the read
 * failed (a quiet note — never breaks the page). Error text (`detail`) only
 * reaches Admins (the server leaves it out for everyone else).
 */
export function ReminderHistory({
  entries,
  failed = false,
  linkTasks = false,
  emptyText = 'No reminders or escalations sent in the last 90 days.',
  templateName,
  paged = false,
}: {
  entries: readonly ReminderEntry[] | null;
  failed?: boolean;
  /** Store page: each task entry links to its task. */
  linkTasks?: boolean;
  emptyText?: string;
  /** Store page: which audit an audit email was about (QA 2026-10-25 — two live templates read alike). */
  templateName?: (templateId: number) => string;
  /** Store page: ten at a time (owner ruling 2026-10-29). */
  paged?: boolean;
}) {
  const pager = usePager(entries, entries, paged ? undefined : Number.MAX_SAFE_INTEGER);
  if (failed) return <p className="muted">Couldn’t load the reminder history.</p>;
  if (entries === null) return <p className="muted">Loading…</p>;
  if (entries.length === 0) return <p className="muted">{emptyText}</p>;
  return (
    <>
      <PagerTop pager={pager} />
      <ul className="reminder-history">
        {pager.items.map((e, i) => {
          const audit = e.templateId !== null && templateName ? templateName(e.templateId) : '';
          const label = `${REMINDER_LABELS[e.type] ?? e.type}${audit ? ` — ${audit}` : ''}`;
          return (
            <li key={`${e.at}-${e.type}-${i}`}>
              <span className="muted">{formatDay(e.at)}</span>
              {' · '}
              {linkTasks && e.taskId !== null ? (
                <Link className="entity-link" to={`/tasks/${e.taskId}`}>
                  {label}
                </Link>
              ) : (
                label
              )}
              {e.to.length > 0 && <> → {e.to.join(', ')}</>}
              {!e.sent && (
                <span className="task-badge reminder-failed" title={e.detail || 'The email could not be sent'}>
                  failed
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <PagerBar pager={pager} noun="emails" />
    </>
  );
}
